import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { patchCall } from "../../lib/calls";

/** Long enough that a pause between words is not a request, short enough that
 *  the text is on the server before most calls end. */
const DEBOUNCE_MS = 700;

type SaveState = "clean" | "dirty" | "saving" | "saved" | "failed";

/**
 * The note an operator types while the call is running.
 *
 * It saves itself to the call log. That is the whole point of this component:
 * the box used to be a plain `useState` textarea inside the call window, under
 * a placeholder that read "notes during the call - saved with the call", and
 * every word of it went away when the window closed. An interface that offers
 * somewhere to write things down and then throws them out is worse than one
 * that offers nothing.
 *
 * Saving is debounced rather than hung off a button. Nobody presses save on a
 * note they are still writing, and the moment the call ends is not the typist's
 * to choose - so the only way a button would work is if it were pressed before
 * it was needed. Whatever is still pending is flushed when the box unmounts, so
 * hanging up mid-sentence keeps the sentence.
 */
export function CallNoteBox({
  callLogId,
  append,
  className,
}: {
  callLogId: string | null;
  /**
   * A line the co-pilot panel wants added, with the moment it was clicked.
   *
   * A signal rather than a second writer: this box owns the text and its own
   * debounced save, and a panel that patched `CallLog.note` directly would race
   * the keystrokes being typed here, with one of the two winning silently. The
   * timestamp is what makes clicking the same suggestion twice do something.
   */
  append?: { text: string; at: number } | null;
  className?: string;
}) {
  const t = useT();
  const [text, setText] = useState("");
  const [state, setState] = useState<SaveState>("clean");

  // The write path reads refs, not state: the unmount flush runs after the last
  // render, when the captured `text` would be one keystroke behind.
  const latest = useRef("");
  const stored = useRef("");
  const idRef = useRef(callLogId);
  idRef.current = callLogId;

  const flush = useCallback(async () => {
    const id = idRef.current;
    const value = latest.current;
    if (!id || value === stored.current) return;
    setState("saving");
    try {
      await patchCall(id, { note: value });
      stored.current = value;
      // Anything typed while the request was in flight leaves this dirty again;
      // the debounce below is already armed for it.
      setState(latest.current === value ? "saved" : "dirty");
    } catch {
      setState("failed");
    }
  }, []);

  useEffect(() => {
    if (!callLogId || latest.current === stored.current) return;
    const timer = setTimeout(() => void flush(), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, callLogId, flush]);

  // An appended suggestion goes on its own line at the end and leaves the cursor
  // to the operator. It marks the box dirty exactly like a keystroke does, so the
  // debounce above saves it without a second write path.
  useEffect(() => {
    if (!append?.text) return;
    setText((prev) => {
      const next = prev.trim() ? `${prev.replace(/\s+$/, "")}\n${append.text}` : append.text;
      latest.current = next;
      return next;
    });
    setState("dirty");
  }, [append?.at, append?.text]);

  // The last word typed before the window closed is worth one more request.
  useEffect(() => () => void flush(), [flush]);

  return (
    <div className={cn("w-full rounded-3xl bg-[var(--crm-sunk)] px-[18px] py-3.5", className)}>
      <textarea
        value={text}
        onChange={(e) => {
          latest.current = e.target.value;
          setText(e.target.value);
          setState("dirty");
        }}
        placeholder={t("calls.dial.notes")}
        className="block h-[62px] w-full resize-none border-0 bg-transparent text-[13.25px] leading-[1.55] outline-none"
      />
      <div className="flex h-4 items-center gap-1.5 text-[11px] text-muted-foreground">
        {state === "saving" ? (
          <>
            <Loader2 className="size-3 animate-spin" />
            {t("calls.dial.noteSaving")}
          </>
        ) : state === "saved" ? (
          <>
            <Check className="size-3" />
            {t("calls.dial.noteSaved")}
          </>
        ) : state === "failed" ? (
          <span className="flex items-center gap-1.5 text-destructive">
            <TriangleAlert className="size-3" />
            {t("calls.dial.noteFailed")}
          </span>
        ) : null}
      </div>
    </div>
  );
}
